import React, { useState, useMemo } from 'react';
import { getToken, apiFetch } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Card,
  CardContent,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import TableChartIcon from '@mui/icons-material/TableChart';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  normalizarCompetencia,
  extrairAnoCompetencia,
  MESES_COMPETENCIA,
} from '../../utils/competenciaUtils';

const STATUS_LABELS = {
  pendente_avaliacao: 'Pendente Avaliação',
  pendente_pagamento: 'Pendente Pagamento',
  pago: 'Concluído'
};

const STATUS_COLORS = {
  pendente_avaliacao: '#ff9800',
  pendente_pagamento: '#2196f3',
  pago: '#4caf50'
};

const TIPO_FLUXO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'terceirizada', label: 'Terceirizada' },
  { value: 'eventual', label: 'Eventual' },
  { value: 'corporativo', label: 'Corporativo' },
  { value: 'servicoMedico', label: 'Serviço Médico' }
];

const MESES = MESES_COMPETENCIA;

const formatCurrency = (v) =>
  (parseFloat(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

const sortCompetencias = (arr) => {
  const parse = (s) => {
    const [mesStr, anoStr] = (s || '').split('/');
    const mes = MESES.indexOf(mesStr) + 1;
    const ano = parseInt(anoStr, 10) || 0;
    return ano * 12 + mes;
  };
  return [...arr].sort((a, b) => parse(b) - parse(a));
};

export default function RelatorioMensalView({
  notasFiscais,
  contratos,
  loading,
  onRefresh,
  onBaixarPDFConsolidado,
  onExportarExcel,
  onImprimirRelatorio,
  onCancelarProcesso,
  canCancelarProcesso = false,
  unidadeSelecionada,
  BACKEND_URL
}) {
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroCompetencia, setFiltroCompetencia] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEtapa, setFiltroEtapa] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroContrato, setFiltroContrato] = useState('');
  const [modalDetalheOpen, setModalDetalheOpen] = useState(false);
  const [processoSelecionado, setProcessoSelecionado] = useState(null);
  const [dialogExportarOpen, setDialogExportarOpen] = useState(false);
  const [exportarMes, setExportarMes] = useState('');
  const [exportarAno, setExportarAno] = useState('');
  const [limparCompetenciasInvalidasLoading, setLimparCompetenciasInvalidasLoading] = useState(false);
  const [limparTodasLoading, setLimparTodasLoading] = useState(false);

  const notasComContrato = useMemo(() => {
    if (!notasFiscais || !contratos) return [];
    return notasFiscais.map((nota) => {
      const contrato = contratos.find((c) => c.numeroContrato === nota.numero_contrato);
      return {
        ...nota,
        empresa: contrato?.empresa || '—',
        setor: contrato?.setor || '—',
        terceirizada: contrato?.terceirizada,
        eventual: contrato?.eventual,
        corporativo: contrato?.corporativo,
        servicoMedico: contrato?.servicoMedico
      };
    });
  }, [notasFiscais, contratos]);

  const competenciasDisponiveis = useMemo(() => {
    const set = new Set();
    notasComContrato.forEach((n) => {
      const c = normalizarCompetencia(n.competencia || n.competencia_mes) || n.competencia || n.competencia_mes;
      if (c) set.add(c);
    });
    return sortCompetencias([...set]);
  }, [notasComContrato]);

  const anosDisponiveis = useMemo(() => {
    const set = new Set();
    competenciasDisponiveis.forEach((c) => {
      const ano = extrairAnoCompetencia(c) || (c || '').split('/')[1];
      if (ano) set.add(ano);
    });
    return [...set].sort((a, b) => (b || '').localeCompare(a || ''));
  }, [competenciasDisponiveis]);

  const notasFiltradas = useMemo(() => {
    let list = [...notasComContrato];
    if (filtroMes || filtroCompetencia) {
      list = list.filter((n) => {
        const comp =
          normalizarCompetencia(n.competencia || n.competencia_mes) ||
          n.competencia ||
          n.competencia_mes ||
          '';
        const [mesStr, anoStr] = comp.split('/');
        if (filtroMes && mesStr !== filtroMes) return false;
        if (filtroCompetencia && anoStr !== filtroCompetencia) return false;
        return true;
      });
    }
    if (filtroTipo) {
      const t = filtroTipo.toLowerCase();
      list = list.filter((n) => getTipoFluxo(n).toLowerCase().includes(t));
    }
    if (filtroEtapa) list = list.filter((n) => (n.status || '').toLowerCase() === filtroEtapa.toLowerCase());
    if (filtroStatus) list = list.filter((n) => (n.status || '').toLowerCase() === filtroStatus.toLowerCase());
    if (filtroContrato && filtroContrato.trim()) {
      const t = filtroContrato.trim().toLowerCase();
      list = list.filter(
        (n) =>
          (n.numero_contrato || '').toLowerCase().includes(t) ||
          (n.empresa || '').toLowerCase().includes(t)
      );
    }
    return list;
  }, [notasComContrato, filtroMes, filtroCompetencia, filtroTipo, filtroEtapa, filtroStatus, filtroContrato]);

  const notasPorMes = useMemo(() => {
    const byMes = {};
    notasFiltradas.forEach((n) => {
      const mes =
        normalizarCompetencia(n.competencia || n.competencia_mes) ||
        n.competencia ||
        n.competencia_mes ||
        'Sem competência';
      if (!byMes[mes]) byMes[mes] = [];
      byMes[mes].push(n);
    });
    return sortCompetencias(Object.keys(byMes)).map((mes) => ({
      competencia: mes,
      notas: byMes[mes]
    }));
  }, [notasFiltradas]);

  const totaisGeral = useMemo(() => {
    const n = notasFiltradas;
    const processos = n.length;
    const valorBruto = n.reduce((s, x) => s + (parseFloat(x.valor_bruto) || 0), 0);
    const valorLiquido = n.reduce((s, x) => s + (parseFloat(x.valor_liquido) || 0), 0);
    return { processos, valorBruto, valorLiquido };
  }, [notasFiltradas]);

  const competenciasInvalidas = useMemo(() => {
    const set = new Set();
    (notasComContrato || []).forEach((n) => {
      const c = (n.competencia || n.competencia_mes || '').toString().trim();
      if (c && /^\d+$/.test(c)) set.add(c);
    });
    return [...set];
  }, [notasComContrato]);

  const handleLimparCompetenciasInvalidas = async () => {
    if (!window.confirm(`Remover ${competenciasInvalidas.length} competência(s) inválida(s) (ex: ${competenciasInvalidas.slice(0, 3).join(', ')}${competenciasInvalidas.length > 3 ? '...' : ''})? Essas notas serão excluídas permanentemente.`)) return;
    setLimparCompetenciasInvalidasLoading(true);
    try {
      const res = await apiFetch(`${BACKEND_URL}/api/notas-fiscais/limpar-competencias-invalidas`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao limpar');
      if (typeof onRefresh === 'function') onRefresh();
    } catch (err) {
      alert(err.message || 'Erro ao limpar competências inválidas');
    } finally {
      setLimparCompetenciasInvalidasLoading(false);
    }
  };

  const handleLimparTodasNotas = async () => {
    if (!window.confirm('ATENÇÃO: Remover TODAS as notas fiscais da sua unidade? Esta ação não pode ser desfeita. Use isso para refazer a importação pela planilha.')) return;
    setLimparTodasLoading(true);
    try {
      const path = withUnidade('/api/notas-fiscais/todas?confirm=1', unidadeSelecionada);
      const res = await apiFetch(path, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao limpar');
      alert(
        data.quantidade != null
          ? `${data.message || 'Limpeza concluída.'} (${data.quantidade} nota(s))`
          : data.message || 'Limpeza concluída.'
      );
      // Zera a lista local imediatamente (evita tela antiga se o refresh falhar)
      if (typeof onRefresh === 'function') {
        await onRefresh();
      }
    } catch (err) {
      alert(err.message || 'Erro ao limpar todas as notas');
    } finally {
      setLimparTodasLoading(false);
    }
  };

  function getTipoFluxo(nota) {
    const is = (v) => v === 1 || v === true || v === '1' || v === 'true';
    if (is(nota.servicoMedico)) return 'Serviço Médico';
    if (is(nota.corporativo)) return 'Corporativo';
    if (is(nota.eventual)) return 'Eventual';
    if (is(nota.terceirizada)) return 'Terceirizada';
    return '—';
  }

  const handleAbrir = (nota) => {
    setProcessoSelecionado(nota);
    setModalDetalheOpen(true);
  };

  const openPdfComAuth = async (url) => {
    const token = getToken();
    try {
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Erro ao abrir PDF. Faça login novamente.');
        return;
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      alert('Erro ao abrir PDF.');
    }
  };

  const handleBaixarPDF = (notaId, e) => {
    if (e) e.stopPropagation();
    if (onBaixarPDFConsolidado) onBaixarPDFConsolidado(notaId);
    else openPdfComAuth(`${BACKEND_URL}/api/notas-fiscais/${notaId}/pdf-consolidado`);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} color="primary" sx={{ mb: 1 }}>
        Relatório mensal
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Lista de processos de pagamento (notas fiscais) por competência/mês.
      </Typography>

      <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <TextField
          size="small"
          placeholder="Buscar por contrato ou nome do contrato"
          value={filtroContrato}
          onChange={(e) => setFiltroContrato(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Mês</InputLabel>
          <Select value={filtroMes} label="Mês" onChange={(e) => setFiltroMes(e.target.value)}>
            <MenuItem value="">Todos os meses</MenuItem>
            {MESES.map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Competência</InputLabel>
          <Select value={filtroCompetencia} label="Competência" onChange={(e) => setFiltroCompetencia(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {anosDisponiveis.map((ano) => (
              <MenuItem key={ano} value={ano}>{ano}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Tipo de fluxo</InputLabel>
          <Select value={filtroTipo} label="Tipo de fluxo" onChange={(e) => setFiltroTipo(e.target.value)}>
            {TIPO_FLUXO_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Etapa atual</InputLabel>
          <Select value={filtroEtapa} label="Etapa atual" onChange={(e) => setFiltroEtapa(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="pendente_avaliacao">Pendente Avaliação</MenuItem>
            <MenuItem value="pendente_pagamento">Pendente Pagamento</MenuItem>
            <MenuItem value="pago">Concluído</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filtroStatus} label="Status" onChange={(e) => setFiltroStatus(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="pendente_avaliacao">Pendente Avaliação</MenuItem>
            <MenuItem value="pendente_pagamento">Pendente Pagamento</MenuItem>
            <MenuItem value="pago">Concluído</MenuItem>
          </Select>
        </FormControl>
        <Button size="small" variant="contained" startIcon={<RefreshIcon />} onClick={onRefresh} disabled={loading}>
          ATUALIZAR
        </Button>
        <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={onImprimirRelatorio}>
          IMPRIMIR RELATÓRIO
        </Button>
        <Button size="small" variant="outlined" startIcon={<TableChartIcon />} onClick={() => setDialogExportarOpen(true)}>
          EXPORTAR EXCEL
        </Button>
        {competenciasInvalidas.length > 0 && (
          <Button size="small" variant="outlined" color="error" startIcon={limparCompetenciasInvalidasLoading ? <CircularProgress size={16} /> : null} onClick={handleLimparCompetenciasInvalidas} disabled={limparCompetenciasInvalidasLoading}>
            {limparCompetenciasInvalidasLoading ? 'Limpando...' : 'Remover competências inválidas (45689, 45717...)'}
          </Button>
        )}
        <Button size="small" variant="outlined" color="error" startIcon={limparTodasLoading ? <CircularProgress size={16} /> : null} onClick={handleLimparTodasNotas} disabled={limparTodasLoading}>
          {limparTodasLoading ? 'Limpando...' : 'Limpar TODAS as notas (refazer importação)'}
        </Button>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Processos</Typography>
                  <Typography variant="h6" fontWeight={700}>{totaisGeral.processos}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Valor bruto</Typography>
                  <Typography variant="h6" fontWeight={700}>{formatCurrency(totaisGeral.valorBruto)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Valor líquido</Typography>
                  <Typography variant="h6" fontWeight={700}>{formatCurrency(totaisGeral.valorLiquido)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {notasPorMes.map(({ competencia, notas }) => {
            const valorBrutoMes = notas.reduce((s, n) => s + (parseFloat(n.valor_bruto) || 0), 0);
            const valorLiquidoMes = notas.reduce((s, n) => s + (parseFloat(n.valor_liquido) || 0), 0);
            return (
              <Accordion key={competencia} variant="outlined" sx={{ mb: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ width: '100%', pr: 2 }}>
                    <Typography variant="h6" color="primary" sx={{ mb: 1.5 }}>
                      <CalendarMonthIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      {competencia}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                            <Typography variant="caption" color="text.secondary">Processos</Typography>
                            <Typography variant="subtitle1" fontWeight={700}>{notas.length}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={4}>
                        <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                            <Typography variant="caption" color="text.secondary">Valor bruto</Typography>
                            <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(valorBrutoMes)}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={4}>
                        <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                            <Typography variant="caption" color="text.secondary">Valor líquido</Typography>
                            <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(valorLiquidoMes)}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Contrato / Empresa</TableCell>
                          <TableCell>Nº Processo</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell align="right">Valor Bruto</TableCell>
                          <TableCell align="right">Valor Líquido</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell width={canCancelarProcesso ? 120 : 80} align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {notas.map((nota) => (
                          <TableRow
                            key={nota.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleAbrir(nota)}
                          >
                            <TableCell>{nota.numero_contrato} — {nota.empresa}</TableCell>
                            <TableCell>{nota.numero_processo || '—'}</TableCell>
                            <TableCell>{getTipoFluxo(nota)}</TableCell>
                            <TableCell align="right">{formatCurrency(nota.valor_bruto)}</TableCell>
                            <TableCell align="right">{formatCurrency(nota.valor_liquido)}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={STATUS_LABELS[nota.status] || nota.status || '—'}
                                sx={{ bgcolor: `${STATUS_COLORS[nota.status] || '#9e9e9e'}22`, color: STATUS_COLORS[nota.status] || '#616161' }}
                              />
                            </TableCell>
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="PDF consolidado">
                                <IconButton size="small" onClick={(e) => handleBaixarPDF(nota.id, e)}>
                                  <TableChartIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {canCancelarProcesso && (
                                <Tooltip title="Cancelar processo (setor Contratos)">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => onCancelarProcesso?.(nota)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {notasPorMes.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Nenhuma nota encontrada com os filtros aplicados.
            </Typography>
          )}
        </>
      )}

      <Dialog open={dialogExportarOpen} onClose={() => setDialogExportarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Exportar Excel</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selecione o mês e o ano da competência para exportar.
          </Typography>
          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel>Mês</InputLabel>
            <Select value={exportarMes} label="Mês" onChange={(e) => setExportarMes(e.target.value)}>
              {MESES.map((m) => (
                <MenuItem key={m} value={m}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Ano</InputLabel>
            <Select value={exportarAno} label="Ano" onChange={(e) => setExportarAno(e.target.value)}>
              {anosDisponiveis.length ? anosDisponiveis.map((ano) => (
                <MenuItem key={ano} value={ano}>{ano}</MenuItem>
              )) : ['2024', '2025', '2026'].map((ano) => (
                <MenuItem key={ano} value={ano}>{ano}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogExportarOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (exportarMes && exportarAno) {
                onExportarExcel(exportarMes, exportarAno);
                setDialogExportarOpen(false);
                setExportarMes('');
                setExportarAno('');
              }
            }}
            disabled={!exportarMes || !exportarAno}
          >
            Exportar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modalDetalheOpen} onClose={() => setModalDetalheOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Detalhe do processo
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setModalDetalheOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {processoSelecionado && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2"><strong>Contrato:</strong> {processoSelecionado.numero_contrato}</Typography>
              <Typography variant="body2"><strong>Empresa:</strong> {processoSelecionado.empresa}</Typography>
              <Typography variant="body2"><strong>Competência:</strong> {processoSelecionado.competencia || processoSelecionado.competencia_mes || '—'}</Typography>
              <Typography variant="body2"><strong>Nº Processo:</strong> {processoSelecionado.numero_processo || '—'}</Typography>
              <Typography variant="body2"><strong>Valor Bruto:</strong> {formatCurrency(processoSelecionado.valor_bruto)}</Typography>
              <Typography variant="body2"><strong>Valor Líquido:</strong> {formatCurrency(processoSelecionado.valor_liquido)}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {STATUS_LABELS[processoSelecionado.status] || processoSelecionado.status}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalDetalheOpen(false)}>Fechar</Button>
          {processoSelecionado && (
            <Button variant="contained" onClick={() => handleBaixarPDF(processoSelecionado.id)}>
              Baixar PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
